import './RenamableHeader.scss';
import React, { useState, useEffect, useRef } from 'react';
import { placeCaretAtEnd } from '../helpers/utility';
import { replaceInvalidCharacters } from '../helpers/helpers';
import { Popup } from 'semantic-ui-react';

export type RenamableHeaderProps = {
  name: string;
  updateName: (name: string) => void;
  maxLength: number;
  readOnly?: boolean;
  save?: () => void;
  updateNameState?: (name: string) => void;
  previewMode?: boolean;
  placeholder?: string;
  groupTitleClassName?: string;
};

export default function RenamableHeader(props: RenamableHeaderProps) {
  const [renameActive, setRenameActive] = useState(false);
  const [ctrlDown, setCtrlDown] = useState(false);
  const title = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (renameActive && title.current) {
      setTimeout(() => {
        placeCaretAtEnd(title.current);
      }, 0);
    }
  }, [renameActive]);

  if (props.previewMode) {
    return (
      <h2>
        <span className="preview-mode">
          <Popup offset={[25, 0]} position="bottom right" content="Submissions will not be recorded." trigger={<span>Preview</span>} />
        </span>
        &nbsp;-&nbsp;
        {props.name}
      </h2>
    );
  }

  if (props.readOnly) {
    return <h2>{props.name}</h2>;
  }

  const keyDown = (e) => {
    if (e.keyCode === 13) {
      e.target.blur();
      e.preventDefault();
    } else if (e.keyCode === 17) {
      setCtrlDown(true);
    } else if (props.maxLength && e.target.innerText.length >= props.maxLength) {
      if (e.keyCode !== 8 && e.keyCode !== 37 && e.keyCode !== 39 && !ctrlDown) {
        e.preventDefault();
      }
    }
  };

  const keyUp = (e) => {
    if (e.keyCode === 17) {
      setCtrlDown(false);
    }
  };

  const onBlur = (e) => {
    const newName = replaceInvalidCharacters(e.target.innerText);
    if (props.name !== newName) {
      props.updateName(newName);

      if (props.updateNameState) {
        props.updateNameState(newName);
      }
    }
    if (props.save) {
      props.save();
    }
    setRenameActive(false);
    setCtrlDown(false);
  };

  const onPaste = (e) => {
    if (e.target.innerText.length - window.getSelection().toString().length + e.clipboardData.getData('Text').length > props.maxLength) {
      e.preventDefault();
    }
  };

  if (renameActive) {
    return (
      <h2 contentEditable onBlur={onBlur} onKeyDown={keyDown} onKeyUp={keyUp} onPaste={onPaste} suppressContentEditableWarning className="editable" ref={title}>
        {props.name}
      </h2>
    );
  }
  return (
    <div className="title-rename-wrapper">
      {props.placeholder && props.name?.trim() === '' ? (
        <div className="placeholder-container">
          <span className="placeholder">{props.placeholder}</span>
          <a className="title-rename" tabIndex={-1} onClick={() => setRenameActive(true)}>
            <i className="icon write" />
          </a>
        </div>
      ) : (
        <>
          <h2 title={props.name} className={props.groupTitleClassName}>
            {props.name}
          </h2>
          <a className="title-rename" tabIndex={-1} onClick={() => setRenameActive(true)}>
            <i className="icon write" />
          </a>
        </>
      )}
    </div>
  );
}
